import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import {
  designColors,
  designFontFamily,
  designWeights,
  designRadii,
} from "services/constants/designTokens";
import { detailAccentPalette, detailSectionLabelSx } from "components/DesignSystem/RequestDetailField";

dayjs.extend(customParseFormat);

const formatProfileDate = (value) => {
  if (!value) return null;
  const date = /^\d{2}-\d{2}-\d{4}$/.test(value)
    ? dayjs(value, "DD-MM-YYYY", true)
    : dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "-";
};

// Compact grid field cell — deliberately not RequestDetailField (that
// component bakes in a full-width Divider meant for a single vertical list;
// this view uses a 2-column grid instead, so cell separation comes from
// Grid spacing, not dividers). Same icon-chip/typography language as
// RequestDetailField so it still reads as part of the same design system.
const ProfileField = ({ icon: Icon, label, value, accent }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Mui.Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: accent.bg,
        color: accent.color,
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Typography
        sx={{
          fontSize: 12,
          fontWeight: designWeights.semibold,
          color: designColors.slate600,
          fontFamily: designFontFamily,
          lineHeight: 1.25,
        }}
      >
        {label}
      </Mui.Typography>
      <Mui.Typography
        sx={{
          mt: 0.35,
          fontSize: 14,
          fontWeight: designWeights.semibold,
          color: designColors.slate900,
          fontFamily: designFontFamily,
          lineHeight: 1.35,
          overflowWrap: "anywhere",
        }}
      >
        {value === null || value === undefined || value === "" ? "-" : value}
      </Mui.Typography>
    </Mui.Box>
  </Mui.Stack>
);

// Shared read-only personal details for My Profile and the no-access landing
// page. Always loads the signed-in user; editing stays in Administration > Users.
export const ProfileEditView = () => {
  const { domain, token, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${domain}person/${userDetails.id}/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        if (response.ok) {
          setProfile(await response.json());
        } else {
          showToast("error", "Couldn't load your profile. Please refresh the page.");
        }
      } catch (_error) {
        console.error("[ProfileEditView] operation failed:", _error);
        showToast("error", "Couldn't load your profile. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    if (userDetails?.id) {
      fetchProfile();
    }
  }, [domain, token, userDetails?.id]);

  if (loading) {
    return (
      <Mui.Box sx={{ py: 6 }}>
        <Loader info="Loading..." />
      </Mui.Box>
    );
  }

  const dob = formatProfileDate(profile?.birthday);
  const details = profile || userDetails || {};
  const fullName = details.full_name ||
    [details.first_name, details.last_name].filter(Boolean).join(" ");
  const employeeId = details.EmployeeID ?? details.employee_id;
  const sections = [
    {
      title: "Employment Information",
      fields: [
        ["Employee ID", employeeId],
        ["User Code", details.user_code],
        ["Role", details.role],
        ["Type", details.type],
        ["Date of Joining", formatProfileDate(details.date_of_join), CakeOutlinedIcon],
        ["Department", details.department],
        ["Department Lead", details.department_lead == null ? null : details.department_lead ? "Yes" : "No"],
        ["Department Lead Name", details.department_lead_name],
      ],
    },
    {
      title: "Personal Information",
      fields: [
        ["First Name", details.first_name],
        ["Last Name", details.last_name],
        ["Email", details.email],
        ["Phone Number", details.phone_number, PhoneOutlinedIcon],
        ["Gender", details.gender, WcOutlinedIcon],
        ["Date of Birth", dob, CakeOutlinedIcon],
        ["Address", details.address, HomeOutlinedIcon, 12],
        ["Country", details.country, PinDropOutlinedIcon],
        ["State", details.state, PinDropOutlinedIcon],
        ["Pincode", details.pincode, PinDropOutlinedIcon],
      ],
    },
  ];
  const firstLetter = (profile?.first_name || userDetails?.first_name || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <Mui.Box sx={{ px: 3, py: 2.5 }}>
      <Mui.Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2.5,
          mb: 2.5,
          borderRadius: designRadii.card,
          background: `linear-gradient(135deg, ${designColors.tealTint} 0%, ${designColors.white} 100%)`,
          border: `1px solid ${designColors.slate200}`,
        }}
      >
        <Mui.Avatar
          alt={fullName || firstLetter}
          src={details.image_url}
          sx={{
            width: 56,
            height: 56,
            bgcolor: designColors.tealDark,
            fontSize: 22,
            fontFamily: designFontFamily,
          }}
        >
          {firstLetter}
        </Mui.Avatar>
        <Mui.Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <Mui.Typography
            sx={{
              fontSize: 17,
              fontWeight: designWeights.heading,
              fontFamily: designFontFamily,
              color: designColors.slate900,
              lineHeight: 1.3,
            }}
          >
            {fullName || "-"}
          </Mui.Typography>
          <Mui.Typography
            sx={{
              mt: 0.25,
              fontSize: 13,
              fontFamily: designFontFamily,
              color: designColors.slate600,
            }}
          >
            {[employeeId, details.role].filter(Boolean).join(" • ")}
          </Mui.Typography>
          <Mui.Typography
            sx={{
              mt: 0.1,
              fontSize: 12.5,
              fontFamily: designFontFamily,
              color: designColors.slate500,
            }}
          >
            {details.email || "-"}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Box>

      {sections.map(({ title, fields }) => (
        <Mui.Box key={title} sx={{ mb: 2.5 }}>
          <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.5 }}>
            {title}
          </Mui.Typography>
          <Mui.Box sx={{ p: 2.5, borderRadius: designRadii.card, border: `1px solid ${designColors.slate200}`, backgroundColor: designColors.white }}>
            <Mui.Grid container spacing={2.5}>
              {fields.map(([label, value, icon = PersonOutlineOutlinedIcon, width = 6], index) => (
                <Mui.Grid item xs={12} sm={width} key={label}>
                  <ProfileField
                    icon={icon}
                    label={label}
                    value={value}
                    accent={Object.values(detailAccentPalette)[index % Object.keys(detailAccentPalette).length]}
                  />
                </Mui.Grid>
              ))}
            </Mui.Grid>
          </Mui.Box>
        </Mui.Box>
      ))}
    </Mui.Box>
  );
};

export default ProfileEditView;
